import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrderMobileCardShell from "./OrderMobileCardShell";
import { rowBetweenDateInputs } from "./orderFilterHelpers";
import StakingService from "../../../api/services/StakingService";
import StakingPayoutHistoryModal from "../Staking/StakingPayoutHistoryModal";
import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";
import { getTotalInvestedAmount } from "../Staking/stakingStakeHelpers";
import { formatSpotActivityDateTime } from "./spotMeActivityHelpers";
import "../SpotOrders/spotOrders.css";

const ORDER_DESKTOP_COL_COUNT = 5;

function parseDec(val) {
  if (val == null) return "0";
  if (typeof val === "object" && val.$numberDecimal != null) return val.$numberDecimal;
  return String(val);
}

function formatAmount(val, decimals = 8) {
  const n = parseFloat(parseDec(val));
  if (isNaN(n)) return "0";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: decimals });
}

function resolveIcon(iconPath, currency) {
  if (!iconPath) {
    return `/images/staking_img/${String(currency || "usdt").toLowerCase()}_icon.svg`;
  }
  return resolvePublicUploadUrl(iconPath)
    || `/images/staking_img/${String(currency || "usdt").toLowerCase()}_icon.svg`;
}

function DatePickerCell({ value, onChange, ariaLabel, inputRef }) {
  return (
    <div className="so-dateCell">
      <span className="so-dateCell__text" aria-hidden="true">{value}</span>
      <input
        ref={inputRef}
        type="date"
        className="so-dateCell__native"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
      />
    </div>
  );
}

const StakingOrderHistory = () => {
  const today = new Date();
  const threeMonthsAgo = new Date(today);
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  const fmtDate = (d) => d.toISOString().slice(0, 10);

  const [coin, setCoin] = useState("all");
  const [dateFrom, setDateFrom] = useState(fmtDate(threeMonthsAgo));
  const [dateTo, setDateTo] = useState(fmtDate(today));
  const [openDropdown, setOpenDropdown] = useState(null);

  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [payoutViewPosition, setPayoutViewPosition] = useState(null);

  const dateFromRefDesk = useRef(null);
  const dateToRefDesk = useRef(null);
  const dateFromRefMob = useRef(null);
  const dateToRefMob = useRef(null);

  const fetchPositions = useCallback(async (page = 1) => {
    setLoading(true);
    setError("");
    const res = await StakingService.getMyPositions(page, 20);
    if (res?.success && Array.isArray(res.data)) {
      setPositions(res.data);
      if (res.pagination) setPagination((prev) => ({ ...prev, ...res.pagination, page }));
    } else {
      setError(res?.message || "Failed to load staking orders.");
      setPositions([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPositions(1); }, [fetchPositions]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      if (!(e.target instanceof Element)) return;
      if (!e.target.closest("[data-so-dropdown]")) setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpenDropdown(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const coinOptions = useMemo(() => {
    const coins = [...new Set(positions.map((p) => p.currency).filter(Boolean))];
    return [{ value: "all", label: "All" }, ...coins.map((c) => ({ value: c, label: c }))];
  }, [positions]);

  const filteredRows = useMemo(() => {
    return positions.filter((r) => {
      if (coin !== "all" && String(r.currency || "").toUpperCase() !== String(coin).toUpperCase()) return false;
      if (!rowBetweenDateInputs(r.startDate, dateFrom, dateTo)) return false;
      return true;
    });
  }, [positions, coin, dateFrom, dateTo]);

  const resetFilters = () => {
    setCoin("all");
    setDateFrom(fmtDate(threeMonthsAgo));
    setDateTo(fmtDate(today));
  };

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (el && typeof el.showPicker === "function") {
      try { el.showPicker(); return; } catch { /* fall through */ }
    }
    el?.click();
  };

  const hasExpandableTopUp = (row) => Array.isArray(row?.topUpHistory) && row.topUpHistory.length >= 1;

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getTopUpAmount = (item) => item?.topUpAmount ?? item?.amount ?? item?.investAmount ?? "0";
  const getTopUpDate = (item) => item?.topUpAt ?? item?.date ?? item?.topUpDate ?? item?.toppedUpAt ?? null;
  const getTopUpType = (item) => item?.type ?? item?.transactionType ?? "Top Up";

  const renderViewButton = (row, stopRowToggle = false) => (
    <button
      type="button"
      className="so-stkActionBtn so-stkActionBtn--view"
      title="View payout history"
      onClick={(e) => {
        if (stopRowToggle) e.stopPropagation();
        setPayoutViewPosition(row);
      }}
    >
      <i className="ri-eye-line" aria-hidden="true" />
      View
    </button>
  );

  const renderTypeCell = (expandable, isExpanded) => (
    <span className="so-stkHistType">
      <span className="so-stakeType so-stakeType--flexible">Staking</span>
      {expandable ? (
        <i
          className={`ri-arrow-${isExpanded ? "up" : "down"}-s-line so-stkHistType__arrow`}
          aria-hidden="true"
        />
      ) : null}
    </span>
  );

  const renderTopUpHistoryBlock = (row, icon) => (
    <div className="so-stkTopUpHistory so-stkTopUpHistory--mobile">
      <div className="so-stkTopUpHistory__title">Top Up History</div>
      {row.topUpHistory.map((item, idx) => (
        <div key={item?._id || item?.id || `topup-m-${row._id}-${idx}`} className="so-stkTopUpHistory__card">
          <div className="so-mobileCard__row">
            <span>Coin</span>
            <span className="so-stakingCoin">
              <img src={icon} alt={row.currency} width={24} height={24} className="so-stakingCoin__icon"
                onError={(e) => { e.target.onerror = null; e.target.src = `/images/staking_img/${String(row.currency || "usdt").toLowerCase()}_icon.svg`; }} />
              <span className="so-num">{row.currency}</span>
            </span>
          </div>
          <div className="so-mobileCard__row">
            <span>Date</span>
            <span className="so-num">{formatSpotActivityDateTime(getTopUpDate(item))}</span>
          </div>
          <div className="so-mobileCard__row">
            <span>Invested</span>
            <span className="so-num">{formatAmount(getTopUpAmount(item))} {row.currency}</span>
          </div>
          <div className="so-mobileCard__row">
            <span>Type</span>
            <span className="so-stakeType so-stakeType--locked">{getTopUpType(item)}</span>
          </div>
        </div>
      ))}
    </div>
  );

  const renderDesktopTopUpRows = (row, icon) => (
    <>
      <tr className="so-stkHistTopUpHeaderRow">
        <td colSpan={ORDER_DESKTOP_COL_COUNT}>
          <span className="so-stkTopUpHistory__title">Top Up History</span>
        </td>
      </tr>
      {row.topUpHistory.map((item, idx) => (
        <tr key={item?._id || item?.id || `topup-${row._id}-${idx}`} className="so-stkHistTopUpRow">
          <td>
            <span className="so-stakingCoin">
              <img src={icon} alt={row.currency} width={24} height={24} className="so-stakingCoin__icon"
                onError={(e) => { e.target.onerror = null; e.target.src = `/images/staking_img/${String(row.currency || "usdt").toLowerCase()}_icon.svg`; }} />
              <span className="so-num">{row.currency}</span>
            </span>
          </td>
          <td className="so-num">{formatSpotActivityDateTime(getTopUpDate(item))}</td>
          <td className="so-num">{formatAmount(getTopUpAmount(item))} {row.currency}</td>
          <td className="so-stkHistTypeCell">
            <span className="so-stakeType so-stakeType--locked">{getTopUpType(item)}</span>
          </td>
          <td />
        </tr>
      ))}
    </>
  );

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    const fromRef = keyPrefix === "mob" ? dateFromRefMob : dateFromRefDesk;
    const toRef = keyPrefix === "mob" ? dateToRefMob : dateToRefDesk;
    return (
      <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter staking orders">
        <div className="so-toolbar__left">
          <CustomDropdown
            id={`${pref}stk-hist-dd-coin`} label="Coin" value={coin} options={coinOptions}
            onChange={setCoin} menuKey={`${pref}stk-hist-coin`} openKey={openDropdown} onOpenToggle={setOpenDropdown}
          />
          <div className="so-dateBlock">
            <span className="so-dateBlock__label">Date</span>
            <div className="so-dateRow">
              <DatePickerCell value={dateFrom} onChange={setDateFrom} ariaLabel="Start date" inputRef={fromRef} />
              <span className="so-dateArrow" aria-hidden="true">→</span>
              <DatePickerCell value={dateTo} onChange={setDateTo} ariaLabel="End date" inputRef={toRef} />
              <button type="button" className="so-dateCalBtn" aria-label="Open calendar" onClick={() => openDatePicker(toRef)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.35" />
                  <path d="M3 10h18" stroke="currentColor" strokeWidth="1.35" />
                  <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  const renderRow = (r) => {
    const pkg = r.packageId || {};
    const icon = resolveIcon(pkg.iconPath, r.currency);
    const expandable = hasExpandableTopUp(r);
    const isExpanded = expandedIds.has(r._id);

    return (
      <React.Fragment key={r._id}>
        <tr
          className={expandable ? `so-stkHistRow--expandable${isExpanded ? " so-stkHistRow--expanded" : ""}` : ""}
          onClick={expandable ? () => toggleExpanded(r._id) : undefined}
          onKeyDown={expandable ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleExpanded(r._id);
            }
          } : undefined}
          tabIndex={expandable ? 0 : undefined}
          role={expandable ? "button" : undefined}
          aria-expanded={expandable ? isExpanded : undefined}
        >
          <td>
            <span className="so-stakingCoin">
              <img src={icon} alt={r.currency} width={24} height={24} className="so-stakingCoin__icon"
                onError={(e) => { e.target.onerror = null; e.target.src = `/images/staking_img/${String(r.currency || "usdt").toLowerCase()}_icon.svg`; }} />
              <span className="so-num">{r.currency}</span>
            </span>
          </td>
          <td className="so-num">{formatSpotActivityDateTime(r.startDate)}</td>
          <td className="so-num">{formatAmount(getTotalInvestedAmount(r))} {r.currency}</td>
          <td className="so-stkHistTypeCell">{renderTypeCell(expandable, isExpanded)}</td>
          <td className="so-stkHistViewCell">{renderViewButton(r, true)}</td>
        </tr>
        {expandable && isExpanded ? renderDesktopTopUpRows(r, icon) : null}
      </React.Fragment>
    );
  };

  const renderMobileCard = (r) => {
    const pkg = r.packageId || {};
    const icon = resolveIcon(pkg.iconPath, r.currency);
    const expandable = hasExpandableTopUp(r);
    const isExpanded = expandedIds.has(r._id);

    return (
      <div key={`m-${r._id}`} className={`so-stkHistMobileCard${expandable ? " so-stkHistMobileCard--expandable" : ""}`}>
        <OrderMobileCardShell desktopColumnCount={ORDER_DESKTOP_COL_COUNT} popupTitle="Staking order">
          <div className="so-mobileCard__row">
            <span>Coin</span>
            <span className="so-stakingCoin">
              <img src={icon} alt={r.currency} width={24} height={24} className="so-stakingCoin__icon"
                onError={(e) => { e.target.onerror = null; e.target.src = `/images/staking_img/${String(r.currency || "usdt").toLowerCase()}_icon.svg`; }} />
              <span className="so-num">{r.currency}</span>
            </span>
          </div>
          <div className="so-mobileCard__row">
            <span>Date</span>
            <span className="so-num">{formatSpotActivityDateTime(r.startDate)}</span>
          </div>
          <div className="so-mobileCard__row">
            <span>Invested</span>
            <span className="so-num">{formatAmount(getTotalInvestedAmount(r))} {r.currency}</span>
          </div>
          <div
            className={`so-mobileCard__row${expandable ? " so-stkHistMobileType--clickable" : ""}`}
            onClick={expandable ? () => toggleExpanded(r._id) : undefined}
            onKeyDown={expandable ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleExpanded(r._id);
              }
            } : undefined}
            role={expandable ? "button" : undefined}
            tabIndex={expandable ? 0 : undefined}
            aria-expanded={expandable ? isExpanded : undefined}
          >
            <span>Type</span>
            {renderTypeCell(expandable, isExpanded)}
          </div>
          <div className="so-mobileCard__row so-stkHistMobileView">
            <span>Action</span>
            {renderViewButton(r)}
          </div>
        </OrderMobileCardShell>
        {expandable && isExpanded ? renderTopUpHistoryBlock(r, icon) : null}
      </div>
    );
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="so-staking-history-title">
            <h1 id="so-staking-history-title" className="so-page__title">Staking</h1>
            {renderToolbar(false, "desk")}
            <div className="so-tableWrap so-tableWrap--staking">
              <table className="so-table so-table--rowLines so-table--staking">
                <thead>
                  <tr>
                    <th scope="col">Coin</th>
                    <th scope="col">Date</th>
                    <th scope="col">Invested</th>
                    <th scope="col">Type</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? (
                      <tr>
                        <td colSpan={ORDER_DESKTOP_COL_COUNT} style={{ textAlign: "center", padding: "40px", color: "#848e9c" }}>
                          Loading...
                        </td>
                      </tr>
                    )
                    : error
                    ? (
                      <tr>
                        <td colSpan={ORDER_DESKTOP_COL_COUNT} style={{ textAlign: "center", padding: "40px", color: "#f6465d" }}>
                          {error}
                        </td>
                      </tr>
                    )
                    : filteredRows.length === 0
                    ? renderEmptyBody()
                    : filteredRows.map(renderRow)}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="staking_pagination" style={{ marginTop: "16px" }}>
                <button type="button" className="page_nav prev_nav"
                  disabled={pagination.page <= 1} onClick={() => fetchPositions(pagination.page - 1)}>‹</button>
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => (
                  <button key={p} type="button"
                    className={`page_num${pagination.page === p ? " active" : ""}`}
                    onClick={() => fetchPositions(p)}>{p}</button>
                ))}
                <button type="button" className="page_nav next_nav"
                  disabled={pagination.page >= pagination.totalPages} onClick={() => fetchPositions(pagination.page + 1)}>›</button>
              </div>
            )}
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Staking</h2>
          <div className="so-mobile__toolbar">
            <SoMobileFilters modalId="mob-staking-history-filters" toolbar={renderToolbar(true, "mob")} onReset={resetFilters} />
          </div>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#848e9c" }}>Loading...</div>
          ) : error ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#f6465d" }}>{error}</div>
          ) : filteredRows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            filteredRows.map(renderMobileCard)
          )}
        </div>
      </div>

      {payoutViewPosition ? (
        <StakingPayoutHistoryModal
          positionId={payoutViewPosition._id || payoutViewPosition.positionId}
          currency={payoutViewPosition.currency}
          onClose={() => setPayoutViewPosition(null)}
        />
      ) : null}
    </div>
  );
};

export default StakingOrderHistory;
